import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { useHeaderState } from '../motion/useHeaderState';

const navigationItems = [
    { path: '/cards', label: 'Cards' },
    { path: '/accounts', label: 'Accounts' },
    { path: '/stablecoin-wallet', label: 'Wallet' },
    { path: '/blog', label: 'Blog' },
    { path: '/about', label: 'About' },
];

const openStore = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
        window.open('https://apps.apple.com/us/app/yunlen/id6751148625', '_blank');
    } else {
        window.open('https://play.google.com/store/apps/details?id=com.yunlen.app', '_blank');
    }
};

/**
 * Floating pill, divided into cells by hairlines. Colours are driven by
 * useHeaderState, which inverts the bar to match the section passing beneath
 * it, so it stays legible over both the black and the white panels.
 */
const Header = () => {
    const { scrolled, hidden, themeUnder } = useHeaderState();
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const panelRef = useRef(null);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => setIsOpen(false), [location.pathname]);

    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && setIsOpen(false);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const isActive = (path) => location.pathname === path;

    return (
        <>
            <header
                className="aug-header"
                data-scrolled={scrolled || isOpen}
                data-hidden={hidden && !isOpen}
                data-theme-under={isOpen ? 'dark' : themeUnder}
            >
                <div className="aug-header-bar">
                    {/* Logo cell */}
                        <div className="aug-header-cell flex items-center pr-4 lg:pr-6">
                            <Link to="/" className="flex items-center gap-2.5 group" aria-label="Yunlen home">
                                <span className="w-8 h-8 rounded-lg bg-[var(--color-accent)] flex items-center justify-center overflow-hidden transition-transform duration-300 group-hover:rotate-[-8deg]">
                                    <img src="/icon-192.png" alt="" className="w-5 h-5 object-contain" />
                                </span>
                                <span className="hidden sm:block font-extrabold tracking-[-0.04em] text-lg leading-none">
                                    YUNLEN
                                </span>
                            </Link>
                        </div>

                        {/* Nav cell */}
                        <nav className="aug-header-cell hidden md:flex items-stretch pl-2">
                            {navigationItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className="aug-navlink aug-label"
                                    aria-current={isActive(item.path) ? 'page' : undefined}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </nav>

                        {/* Spacer keeps the CTA hard right */}
                        <div className="flex-1" />

                        {/* CTA cell */}
                        <div className="aug-header-cell hidden md:flex items-center pl-4">
                            <button
                                type="button"
                                onClick={openStore}
                                className="aug-label inline-flex items-center gap-2 h-9 px-4 rounded-full bg-[var(--color-accent)] text-[var(--color-black)] font-bold transition-transform duration-300 hover:-translate-y-0.5"
                            >
                                Download
                                <ArrowUpRight className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {/* Mobile toggle */}
                        <div className="aug-header-cell flex md:hidden items-center pl-4">
                            <button
                                type="button"
                                onClick={() => setIsOpen((v) => !v)}
                                aria-label={isOpen ? 'Close menu' : 'Open menu'}
                                aria-expanded={isOpen}
                                className="inline-flex items-center justify-center w-10 h-10 -mr-2"
                            >
                                {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </button>
                        </div>
                </div>
            </header>

            {/* Full-screen mobile panel — nav set at display scale */}
            <div
                ref={panelRef}
                data-theme="dark"
                className={`aug-theme md:hidden fixed inset-0 z-50 flex flex-col transition-[opacity,visibility] duration-300 ${
                    isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                }`}
                style={{ paddingTop: '5.5rem' }}
                aria-hidden={!isOpen}
            >
                <nav className="flex-1 overflow-y-auto px-5 pt-8 pb-10">
                    {navigationItems.map((item, i) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsOpen(false)}
                            className="block border-t border-[var(--theme-line)] py-5"
                            style={{
                                transition: 'transform 0.5s var(--ease-aug), opacity 0.5s var(--ease-aug)',
                                transitionDelay: `${isOpen ? 0.06 * i + 0.05 : 0}s`,
                                transform: isOpen ? 'translateY(0)' : 'translateY(14px)',
                                opacity: isOpen ? 1 : 0,
                            }}
                        >
                            <span className="flex items-center justify-between gap-4">
                                <span
                                    className="aug-h2"
                                    style={{ color: isActive(item.path) ? 'var(--color-accent)' : undefined }}
                                >
                                    {item.label}
                                </span>
                                <span className="aug-label text-[var(--theme-muted)]">
                                    0{i + 1}
                                </span>
                            </span>
                        </Link>
                    ))}
                    <div className="border-t border-[var(--theme-line)] pt-8 mt-2">
                        <button
                            type="button"
                            onClick={() => {
                                setIsOpen(false);
                                openStore();
                            }}
                            className="aug-btn w-full"
                        >
                            Download the app
                            <ArrowUpRight className="w-4 h-4" />
                        </button>
                    </div>
                </nav>
            </div>
        </>
    );
};

export default Header;
